<script lang="ts">
  import { getContext } from "svelte";
  import { CREATE_NEW_ACCOUNT_CONTEXT_NAME } from "../../utils";
  import type { CreateNewAccountContext } from "../../type";
  import type { PlanRecurringPeriod } from "../../../../../app/store/store-plan";
  import RadioCheckIndicator from "../../../common/RadioCheckIndicator.svelte";
  import SelectPlanTierDropdown from "./select-plan-type/SelectPlanTierDropdown.svelte";
  import RadioOptionStyle from "../../../common/RadioOptionStyle.svelte";
  import { plansDataStore } from "../../../../../app/store/store-plan";

  const context: CreateNewAccountContext = getContext(
    CREATE_NEW_ACCOUNT_CONTEXT_NAME
  );
  const discountRate = 0;
  let pages = context.pages;
  let planDuration = context.getPlanDuration();
  let planTier = context.getPlanTier();
  let discount: number = 0;
  let planCost: number = 0;
  let numberOfLicenses: number = 0;
  let licensePrice: number = 0;
  let cost: number = 0;
  let totalPayment: number = 0;
  let isInit: boolean = false;

  const options: {
    name: "planDuration";
    value: PlanRecurringPeriod;
    label: string;
  }[] = [
    {
      name: "planDuration",
      value: "monthly",
      label: "Monthly",
    },
    {
      name: "planDuration",
      value: "yearly",
      label: "Yearly",
    },
  ];

  function round(v: number): number {
    return Math.round((v + Number.EPSILON) * 100) / 100;
  }

  function updateCost() {
    planCost = context.getPlanCost();
    numberOfLicenses = context.getNumberOfLicenses();
    licensePrice = round(context.getLicensePrice());
    cost = planCost + licensePrice * numberOfLicenses;
    discount = round(cost * discountRate);
    totalPayment = round(cost - discount);
    context.totalCost.set(totalPayment);
  }

  $: if (planDuration !== context.getPlanDuration()) {
    context.setPlanDuration(planDuration);
    updateCost();
  }
  $: if (planTier !== context.getPlanTier()) {
    context.setPlanTier(planTier);
    updateCost();
  }
  $: if (!isInit && $pages && $plansDataStore) {
    planTier = context.getPlanTier();
    planDuration = context.getPlanDuration();
    updateCost();
    isInit = true;
  }
</script>

<div class="summary">
  <div class="mb-5 px-8 text-[18px] font-medium text-dark-300">Summary</div>

  <div class="plan-duration px-8">
    <div class="radio-group" role="radiogroup">
      {#each options as option}
        {@const selected = planDuration === option.value}

        <label class:selected>
          <input bind:group={planDuration} type="radio" value={option.value} name={option.name}/>
          <div class="content">
            <span class="capitalize">{option.label}</span>
          </div>
          <div class="radio-indicator">
            <RadioCheckIndicator active={selected} />
          </div>
        </label>
      {/each}
    </div>
  </div>

  <div class="mt-3 px-8 h-11">
    <SelectPlanTierDropdown bind:planTier />
  </div>

  <div class="w-full h-px my-6 bg-light-100" />

  <div class="px-8">
    <div class="plan-cost mb-3">
      <div class="text-dark-100">Plan Cost</div>
      <div class="text-dark-300 font-medium">$ {planCost}</div>
    </div>

    <div class="license-cost mb-3">
      <div class="text-dark-100">
        Total Licenses
        <span class="text-primary-indigo-blue">({numberOfLicenses})</span>
      </div>
      <div class="text-dark-300 font-medium">
        $ {round(licensePrice * numberOfLicenses)}
      </div>
    </div>

    <div class="discount">
      <div class="text-dark-100">Discount</div>
      <div class="text-tertiary-error font-medium">-$ {discount}</div>
    </div>

    <div class="w-full h-px my-2.5 bg-light-100" />

    <div class="total-cost mt-1">
      <div class="text-dark-100">Total</div>
      <div class="text-dark-300 font-medium">$ {totalPayment}</div>
    </div>
  </div>
</div>

<RadioOptionStyle />

<style lang="postcss">
  .summary {
    @apply py-6 rounded bg-white mb-12;
    box-shadow: 1px 2px 12px rgba(55, 84, 170, 0.12);
    height: fit-content;
  }

  .radio-group {
    @apply mt-3 flex flex-row items-center flex-shrink flex-grow-0 gap-2;
    z-index: 0;
  }

  .radio-group label {
    width: 200px;
  }

  .plan-cost,
  .license-cost,
  .discount,
  .total-cost {
    @apply flex flex-row items-center justify-between;
  }
</style>
